import {
  escapeNewLine,
  formatDate,
  formatNZD,
  sanitizeText,
} from "common/utils";
import React, { useEffect, useRef } from "react";
import { InvoiceExportSummary } from "types/model.types";

interface IAdminExportInvoicePageProps {
  data: InvoiceExportSummary;
  onDownloadComplete?: () => void;
}

const AdminExportInvoicePage: React.FC<IAdminExportInvoicePageProps> = ({
  data,
  onDownloadComplete,
}) => {
  const exportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!data || !exportRef.current) return;

    const htmlContent = `
    <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: Verdana, Arial, sans-serif; font-size: 12pt; }
          h2 { font-size: 16pt; font-weight: bold; margin: 20px!important; text-align:center; }
          table { border-collapse: collapse; width: 100%; }
          td, th {border:0px; padding: 4px; vertical-align: top; }
          .title {font-size: 2.0em; border-bottom: 2px solid #000000; margin:0; }
          .subtitle {text-transform:uppercase!important;}
          .stock-out { font-weight:bold; color:red;}
          .page-break { page-break-before: always; border-top:1px dotted green; }
          table thead {display: table-row-group;}
          table thead tr td {font-weight:bold}
          table tfoot {display: table-footer-group;}
          table tbody {display: table-row-group;}
          table td, table td > em {text-align:left}
          em, .em {font-weight: bold;}
          .alignr {text-align:right;}
          .red {color:red;}
          td.notes, td.notes > div { white-space: pre-wrap; text-align: left!important }
        </style>
      </head>
      <body>
        ${exportRef.current.innerHTML}
      </body>
    </html>
  `;

    const blob = new Blob([htmlContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Invoice_${data.id}_Export.xls`;
    link.click();

    onDownloadComplete?.();
  }, [data]);

  return (
    <div ref={exportRef} style={{ display: "none" }}>
      <h1>John Jacobson Limited</h1>
      <table>
        <tbody>
          <tr>
            <td>
              <em>Phone</em>
            </td>
            <td>
              <em>(04) 569-5248</em>
            </td>
            <td>{"                         "}</td>
            <td>
              <em>107 Hutt Park Road</em>
            </td>
          </tr>
          <tr>
            <td>
              <em>E-Fax</em>
            </td>
            <td>
              <em>(04) 566-3019</em>
            </td>
            <td>{"                         "}</td>
            <td>
              <em>Lower Hutt 5010</em>
            </td>
          </tr>
        </tbody>
      </table>
      <h2>*** Invoice ***</h2>
      <table>
        <tbody>
          <tr>
            <td>
              <em>Date Ordered</em>
            </td>
            <td className="alignr">{formatDate(data.summary.createdOn)}</td>
            <td>
              <em>Tax Invoice No:</em>
            </td>
            <td className="alignr">{data.id}</td>
          </tr>
          <tr>
            <td colSpan={2}></td>
            <td>
              <em>Customer No:</em>
            </td>
            <td className="alignr">{data.summary.invoiceToId}</td>
          </tr>
          <tr>
            <td colSpan={2}></td>
            <td>
              <em>GST:</em>
            </td>
            <td className="alignr">20-898-763</td>
          </tr>
          <tr>
            <td colSpan={4}> </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <em>Invoice To</em>
            </td>
            <td colSpan={2}>
              <em>Deliver To</em>
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <div
                dangerouslySetInnerHTML={{
                  __html: escapeNewLine(
                    [
                      data.invoiceTo.debtorName,
                      data.invoiceTo.addressLine1,
                      data.invoiceTo.addressLine2,
                      data.invoiceTo.addressLine3,
                    ]
                      .filter(Boolean)
                      .join("\n"),
                  ),
                }}
              />
            </td>
            <td colSpan={2}>
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    String(data.invoiceTo).trim() ===
                    String(data.deliverTo).trim()
                      ? data.deliverTo.debtorName
                      : [
                          data.deliverTo.debtorName,
                          data.deliverTo.addressLine1,
                          data.deliverTo.addressLine2,
                          data.deliverTo.addressLine3,
                        ]
                          .filter(Boolean)
                          .join("\n"),
                }}
              />
            </td>
          </tr>
          <tr>
            <td>
              <br />
              <em>Your Order No: </em>
              {data.summary.yourOrderNo ?? data.summary.customerOrderNo}
              <br />
            </td>
          </tr>
        </tbody>
      </table>
      <table>
        <thead>
          <tr>
            <td>Product</td>
            <td>Description</td>
            <td className="alignr">Ordered</td>
            <td className="alignr">Supplied</td>
            <td className="alignr">E/Price</td>
            <td className="alignr">C/Price</td>
            <td className="alignr">Discount %</td>
            <td className="alignr">Total</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td className="notes">
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeText(data.summary.notes),
                }}
              />
            </td>
            <td colSpan={6}></td>
          </tr>
          {data.orderDetails.map((item, index) => (
            <tr key={index}>
              <td>{`${item.id}${item.productCode.toUpperCase()}`}</td>
              <td>{item.description}</td>
              <td className="alignr">{item.ordered}</td>
              <td className="alignr">{item.supplied}</td>
              <td className="alignr">{formatNZD(item.eachPrice)}</td>
              <td className="alignr">{formatNZD(item.cartonPrice)}</td>
              <td className="alignr">
                {item.discount > 0 ? item.discount : ""}
              </td>
              <td className="alignr">{formatNZD(item.total)}</td>
            </tr>
          ))}
          <tr>
            <td></td>
            <td className="notes">
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeText(data.summary.notes2),
                }}
              />
            </td>
            <td colSpan={6}></td>
          </tr>
          <tr>
            <td colSpan={6} />
            <td>Sub Total</td>
            <td>{formatNZD(data.summary.total)}</td>
          </tr>
          <tr>
            <td colSpan={6} />
            <td>GST</td>
            <td className="alignr">{formatNZD(data.summary.gst)}</td>
          </tr>
          <tr>
            <td colSpan={6} />
            <td>Total</td>
            <td className="alignr">
              {formatNZD(data.summary.total + data.summary.gst)}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="red">
        <em>NOTE: </em>THE GOODS SHOWN ON THIS INVOICE WILL CONTINUE TO REMAIN
        THE PROPERTY OF JOHN JACOBSON LTD UNTIL THIS INVOICE HAS BEEN PAID IN
        FULL
      </div>
      <br />
      <div>Direct Credit payments can be made to:</div>
      <div>
        Westpac, Petone, Account <em>03-0543-0207702-00</em>
        <br />
      </div>
      <p>Printed on: {formatDate(new Date().toUTCString())}</p>
    </div>
  );
};

export default AdminExportInvoicePage;
